import { type FC, useCallback, useMemo, useState } from 'react';
import {
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tabs,
} from '@material-ui/core';
import cx from 'classnames';

import type {
  UsageReportSnapshot,
  UsageReportUser,
  UsageReportWorkspace,
} from 'src/graphql/reports/queryUsageReportDashboard';

import { useStyles } from './useStyles';

type SortOrder = 'asc' | 'desc';

interface TabConfig {
  key: keyof UsageReportSnapshot;
  label: string;
  type: 'user' | 'workspace';
}

const TABS: TabConfig[] = [
  { key: 'allocatedLicenses', label: 'Allocated Licenses', type: 'user' },
  { key: 'superUsers30d', label: 'Super Users (30d)', type: 'user' },
  { key: 'activeUsers30d', label: 'Active Users (30d)', type: 'user' },
  { key: 'quarterlyActiveUsers90d', label: 'Quarterly Active (90d)', type: 'user' },
  { key: 'inactiveUsers90d', label: 'Inactive (90d)', type: 'user' },
  { key: 'workspaces90d', label: 'Workspaces (90d)', type: 'workspace' },
];

interface DetailTablesProps {
  className?: string;
  data: UsageReportSnapshot;
}

export const DetailTables: FC<DetailTablesProps> = ({ className, data }) => {
  const styles = useStyles();
  const [activeTab, setActiveTab] = useState(0);
  const [sortField, setSortField] = useState<string>('lastActive');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  const handleSort = useCallback(
    (field: string) => {
      if (sortField === field) {
        setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortField(field);
        setSortOrder('asc');
      }
    },
    [sortField]
  );

  const currentTab = TABS[activeTab];
  const rawData = data[currentTab.key] as (UsageReportUser | UsageReportWorkspace)[];

  const sortedData = useMemo(() => {
    if (!sortField || !rawData) return rawData || [];

    return [...rawData].sort((a, b) => {
      const aVal = (a as unknown as Record<string, string>)[sortField] || '';
      const bVal = (b as unknown as Record<string, string>)[sortField] || '';
      const aIsNA = aVal === 'N/A';
      const bIsNA = bVal === 'N/A';
      if (aIsNA && !bIsNA) return 1;
      if (!aIsNA && bIsNA) return -1;
      const cmp = aVal.localeCompare(bVal);
      return sortOrder === 'asc' ? cmp : -cmp;
    });
  }, [rawData, sortField, sortOrder]);

  const isUserTab = currentTab.type === 'user';

  return (
    <div className={cx(styles.root, className)}>
      <Tabs
        value={activeTab}
        onChange={(_, val) => {
          setActiveTab(val);
          setSortField('lastActive');
          setSortOrder('desc');
        }}
        variant="scrollable"
        scrollButtons="auto"
        className={styles.tabs}
      >
        {TABS.map((tab) => {
          const count = (data[tab.key] as unknown[])?.length ?? 0;
          return (
            <Tab
              key={tab.key}
              className={styles.tab}
              label={
                <span>
                  {tab.label}
                  <span className={styles.badge}>{count}</span>
                </span>
              }
            />
          );
        })}
      </Tabs>

      <TableContainer className={styles.tableContainer}>
        {sortedData.length === 0 ? (
          <div className={styles.empty}>No data available</div>
        ) : (
          <Table
            size="small"
            stickyHeader
          >
            <TableHead>
              <TableRow>
                <TableCell className={styles.headerCell}>
                  <TableSortLabel
                    active={sortField === (isUserTab ? 'email' : 'name')}
                    direction={sortOrder}
                    onClick={() => handleSort(isUserTab ? 'email' : 'name')}
                  >
                    {isUserTab ? 'Email' : 'Name'}
                  </TableSortLabel>
                </TableCell>
                <TableCell className={styles.headerCell}>
                  <TableSortLabel
                    active={sortField === 'lastActive'}
                    direction={sortOrder}
                    onClick={() => handleSort('lastActive')}
                  >
                    Last Active
                  </TableSortLabel>
                </TableCell>
                {isUserTab && (
                  <TableCell className={styles.headerCell}>
                    <TableSortLabel
                      active={sortField === 'lastSignedIn'}
                      direction={sortOrder}
                      onClick={() => handleSort('lastSignedIn')}
                    >
                      Last Signed In
                    </TableSortLabel>
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedData.map((row, idx) => (
                <TableRow key={idx}>
                  <TableCell className={styles.cell}>
                    {isUserTab
                      ? (row as UsageReportUser).email
                      : (row as UsageReportWorkspace).name}
                  </TableCell>
                  <TableCell className={styles.cell}>
                    {(row as UsageReportUser).lastActive ??
                      (row as UsageReportWorkspace).lastActive}
                  </TableCell>
                  {isUserTab && (
                    <TableCell className={styles.cell}>
                      {(row as UsageReportUser).lastSignedIn}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </TableContainer>
    </div>
  );
};
